import { useNavigate } from '@tanstack/react-router'
import { memo, useCallback, useMemo } from 'react'

import type {
  ProjectsDashboardResponse,
  SalesProjectRow,
} from '@brickup/shared/dto'
import { ProjectStatus } from '@brickup/shared/enums'
import { CONFIGURATOR_STATUS_LABELS } from '@brickup/shared/locales/de/enum-labels'
import { NAV } from '@brickup/shared/locales/de/nav'
import { STAFF_DASHBOARD } from '@brickup/shared/locales/de/staff-dashboard'
import { USERS } from '@brickup/shared/locales/de/users'
import { formatDateTime } from '@brickup/shared/utils'
import { projectLink, projectsLink } from './dashboard-links'
import { BadgeRow, dash } from '@/components/base'
import {
  DataTable,
  FILTER_ALL,
  primary,
  secondary,
  subtitle,
  type DataTableColumn,
} from '@/components/Internal/DataTable'
import { Panel, PanelSection } from '@/components/Internal/Panel'
import { ProjectStatusBadge } from '@/components/Internal/Projects/ProjectStatusBadge'
import { LinkButton } from '@/components/LinkButton'
import { COUNTED_PROJECT_STATUSES } from '@/constants/project-status-options'
import { useField } from '@/hooks/use-field'
import { useIsProjectScoped } from '@/hooks/use-project-access'

const COPY = STAFF_DASHBOARD.projects

/**
 * The projects the reader works on: a badge per status and the table under it.
 *
 * Shown by both dashboards that run projects — the advisor's, beside the
 * customer half, and the project manager's on its own — so the rows, the
 * columns and the links are decided once.
 */
export const ActiveProjectsPanel = memo(function ActiveProjectsPanel({
  statusCounts,
  projects,
}: Pick<ProjectsDashboardResponse, 'statusCounts' | 'projects'>) {
  const navigate = useNavigate()
  const columns = useProjectColumns()
  // The figures count what the reader works on, so the lists they lead to
  // open on the same set.
  const scoped = useIsProjectScoped()
  const count = (status: ProjectStatus) => statusCounts[status] ?? 0
  // Stable: the table memoises its rows against them, and both dashboards
  // poll, so a fresh arrow would rebuild every row twice a minute.
  const keyOf = useCallback(
    (project: SalesProjectRow) => String(project.id),
    [],
  )
  const open = useCallback(
    (project: SalesProjectRow) => void navigate(projectLink(project.id)),
    [navigate],
  )

  return (
    <Panel
      size="sm"
      title={STAFF_DASHBOARD.activeProjects}
      subtitle={COPY.projectsSubtitle}
      action={
        <LinkButton
          {...projectsLink({ mine: scoped, status: FILTER_ALL })}
          variant="link"
          size="sm"
        >
          {COPY.allProjects}
        </LinkButton>
      }
    >
      <PanelSection>
        <BadgeRow>
          {COUNTED_PROJECT_STATUSES.map(status => (
            <ProjectStatusBadge
              key={status}
              status={status}
              count={count(status)}
              link={projectsLink({ mine: scoped, status })}
            />
          ))}
        </BadgeRow>
      </PanelSection>
      <DataTable<SalesProjectRow>
        items={projects}
        columns={columns}
        keyExtractor={keyOf}
        resizableColumns={false}
        emptyTitle={COPY.noActiveProjects}
        fit
        onRowPress={open}
      />
    </Panel>
  )
})

function useProjectColumns(): DataTableColumn<SalesProjectRow>[] {
  const status = useField('status')
  const configurator = useField('configuratorStatus')
  const lastSeen = useField('lastSeenAt')

  return useMemo(
    () => [
      {
        key: 'name',
        label: NAV.projects,
        width: 'flex-1',
        render: project => (
          <span className="flex min-w-0 flex-col gap-0.5">
            {primary(project.name)}
            {subtitle(project.customerName ?? dash)}
          </span>
        ),
      },
      {
        key: 'status',
        label: status.label,
        width: 'w-40',
        render: project => <ProjectStatusBadge status={project.status} />,
      },
      {
        key: 'configurator',
        label: configurator.label,
        width: 'w-28',
        render: project =>
          secondary(CONFIGURATOR_STATUS_LABELS[project.configuratorStatus]),
      },
      {
        key: 'lastActivity',
        label: COPY.lastCustomerActivity,
        width: 'w-44',
        render: project =>
          project.lastCustomerActivityAt
            ? secondary(formatDateTime(project.lastCustomerActivityAt))
            : dash,
      },
      {
        key: 'lastSeen',
        label: lastSeen.label,
        width: 'w-44',
        render: project =>
          secondary(
            formatDateTime(project.customerLastSeenAt, USERS.neverSeen),
          ),
      },
    ],
    [status.label, configurator.label, lastSeen.label],
  )
}
